
import { InboxItem } from "../store/slice/inboxSlice";

export const FALLBACK_FRAME_HEIGHT = 96;

export type GovernanceResponseStatus = {
  status: string;
  task_state?: string;
  can_respond?: boolean;
  title?: string;
  message?: string;
};


export function respondSrc(item: InboxItem): string {
  const respond = (item.data?.respond ?? {}) as Record<string, any>;
  const token = respond.token as string | undefined;
  let url = (respond.respond_url || respond.view_url) as string | undefined;
  if (!url && token) {
    const route = item.data?.interaction_type === "inform" ? "view" : "respond";
    url = `${import.meta.env.PUBLIC_BASE_URL ?? ""}/governance/${route}?token=${encodeURIComponent(token)}`;
  }
  if (!url) return "";
  return url + (url.includes("?") ? "&" : "?") + "embed=1";
}

export function responseStatusSrc(item: InboxItem): string {
  const respond = (item.data?.respond ?? {}) as Record<string, any>;
  if (respond.view_url) return "";
  if (respond.status_url) return String(respond.status_url);
  const token = respond.token as string | undefined;
  if (!token) return "";
  const base = import.meta.env.PUBLIC_BASE_URL ?? "";
  return `${base}/governance/respond/status?token=${encodeURIComponent(token)}`;
}

export function trustedActionOrigin(url: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin === window.location.origin ? parsed.origin : "";
  } catch {
    return "";
  }
}


export function trimCss(text: string, accent: string): string {
  const muted = `color-mix(in srgb, ${text} 65%, transparent)`;
  return `
    html { margin: 0 !important; padding: 0 !important; background: transparent !important;
           overflow: hidden !important; }
    /* flow-root so child margins stay inside the body box we measure. */
    body { margin: 0 !important; padding: 0 !important; background: transparent !important;
           display: flow-root !important; height: auto !important; overflow: visible !important;
           font-size: 12px !important; line-height: 1.45 !important; color: ${text} !important;
           text-align: left !important; }
    /* The result page ("Already resolved" / "expired") centres itself for a full
       browser tab; in the row it has to line up with the rest of the text. */
    body > div { max-width: none !important; margin: 0 !important; padding: 0 !important;
                 border: 0 !important; border-radius: 0 !important; background: transparent !important;
                 text-align: left !important; }
    /* The bell row already shows the title and the "needs your response" line. */
    #card > h2, #card > p { display: none !important; }
    body > div > h2 { margin: 0 0 4px !important; font-size: 12.5px !important; color: ${text} !important; }
    body > div > p { margin: 0 !important; font-size: 11.5px !important; color: ${muted} !important; }
    /* The reason/action/environment block — first unnamed div, not the button row. */
    #card > div:not([id]):first-of-type { padding: 8px 10px !important; font-size: 11.5px !important; border-radius: 8px !important; }
    table { font-size: 11px !important; table-layout: fixed !important; }
    td { word-break: break-word !important; }
    textarea, input[type="number"], input[type="date"] {
      margin-top: 10px !important; padding: 8px 10px !important; font-size: 12px !important; line-height: 1.45 !important;
      border-radius: 8px !important; background: #fff !important; outline: none !important;
      transition: border-color .15s ease, box-shadow .15s ease !important; }
    /* Long reasons scroll inside the box instead of stretching the inbox row. */
    textarea { min-height: 64px !important; max-height: 140px !important; resize: none !important;
               overflow-y: auto !important; word-break: break-word !important; }
    textarea:focus, input:focus { border-color: ${accent} !important;
      box-shadow: 0 0 0 3px color-mix(in srgb, ${accent} 18%, transparent) !important; }
    #msg { margin: 6px 0 !important; min-height: 0 !important; font-size: 11.5px !important; }
    #msg:empty { margin: 4px 0 !important; }
    #approve, #reject, #send { padding: 8px 12px !important; font-size: 12px !important; border-radius: 8px !important;
      transition: filter .15s ease !important; }
    #approve:hover, #reject:hover, #send:hover { filter: brightness(.92) !important; }
    #approve:disabled, #reject:disabled, #send:disabled { opacity: .6 !important; cursor: default !important; }
    /* Touch screens: iOS zooms into inputs under 16px, and buttons need a
       finger-sized target. The frame's own width can't tell us this — it is
       narrow on desktop too — so key off the pointer instead. */
    @media (pointer: coarse) {
      textarea, input[type="number"], input[type="date"] { font-size: 16px !important; }
      #approve, #reject, #send { min-height: 40px !important; font-size: 13px !important; }
    }
  `;
}
